/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/stores/calendar.store.ts
 */

/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import { derived, get, writable } from 'svelte/store';

import type { CalendarView, ProviderCalendarsView, ProviderInfo } from '@repo/common';
import { transformProviderCalendars } from '@repo/toolslib/src/utils/calendar';
import { fetchCalendars } from '../data/calendar.data';
import { createStoreHandle, type DataModel } from '../utils/store.util';
import { waitForAuthAndUserId } from './waitForAuth';

import constants from '@repo/sharedlib/src/utils/constants';
const showErrorToast = (..._args: any[]) => {};

const FILENAME = '.providers.calendars.dat';

const dummyCalendars: ProviderCalendarsView = {
  providers: []
};

const UPDATE_FREQUENCY = 60 * 1000; // 1 minute in milliseconds

const handleCalendarError = (_message: string) => {
  showErrorToast('Unable to fetch calendars. Please re-authenticate your calendars.');
};

// Create the calendar store handle
const {
  store: calendarsViewDataModel,
  itemStore: calendarsView,
  setItem: setCalendarsView,
  resetItem: resetCalendarsView,
  updateItem: refreshCalendars
} = createStoreHandle<ProviderCalendarsView>(
  FILENAME,
  dummyCalendars,
  {
    updateTimeout: 5 * 60 * 1000, // 5 minutes
    retryTimeout: 5 * 1000 // 5 seconds
  },
  async (): Promise<
    Omit<DataModel<ProviderCalendarsView>, 'status' | 'lastUpdated'> | undefined
  > => {
    // TODO: Refactor to pass this in
    const $calendarsViewDataModel = get(calendarsViewDataModel);

    // Fetch function responsible for fetching the calendarsView and scheduling an update.
    let providers: ProviderInfo[] | undefined;

    try {
      providers = await fetchCalendars();
    } catch (error) {
      console.debug('Unable to fetch calendars: ', error);
      return;
    }

    if (providers) {
      const dbCalendarIds: string[] = [];
      for (const provider of providers) {
        for (const calendar of provider.calendars) {
          dbCalendarIds.push(calendar.id);
        }
      }
    }
    return {
      nextUpdate: new Date(Date.now() + UPDATE_FREQUENCY).getTime(),
      needsPatch: false,
      item: {
        ...$calendarsViewDataModel.item,
        providers
      } as ProviderCalendarsView
    };
  },
  handleCalendarError
);

// Safe wrapper function that ensures user ID is set before loading
export const safeLoadStoredCalendars = async (_force: boolean = false, _reload: boolean = false) => {
  try {
    // Wait for authentication and user ID to be ready
    await waitForAuthAndUserId().catch((err) => {
      console.debug('Error waiting for auth and user id:', err);
      throw err; // Re-throw to be caught by outer try-catch
    });

    console.log('[provider.calendars.store] Auth and user ID confirmed, loading calendars...');
    await refreshCalendars();
  } catch (error) {
    console.error('[provider.calendars.store] Error loading calendars:', error);
    // Don't throw the error to prevent breaking the app flow
  }
};

let refreshPromise: Promise<boolean> | undefined;

// Safe wrapper function that ensures user ID is set before refreshing
export const safeRefreshCalendars = async (): Promise<boolean> => {
  // Prevent multiple simultaneous refresh calls
  if (refreshPromise) {
    console.log(
      '[provider.calendars.store] Calendar refresh already in progress, returning existing promise...'
    );
    return refreshPromise;
  }
  refreshPromise = _safeRefreshCalendars().then((result) => {
    refreshPromise = undefined;
    return result;
  });
  return refreshPromise;
};

const _safeRefreshCalendars = async (): Promise<boolean> => {
  try {
    console.log('[provider.calendars.store] Waiting for auth...');

    // Wait for authentication and user ID to be ready
    await waitForAuthAndUserId().catch((err) => {
      console.debug('Error waiting for auth and user id:', err);
      throw err; // Re-throw to be caught by outer try-catch
    });

    console.log('[provider.calendars.store] Auth and user ID confirmed, refreshing calendars...');
    const result = await refreshCalendars();
    console.log('[provider.calendars.store] Calendars refreshed:', result);
    return result;
  } catch (error) {
    console.debug('[provider.calendars.store] Error refreshing calendars:', error);
    // Don't throw the error to prevent breaking the app flow
    return false;
  }
};

export const providerCalendars = derived(calendarsView, ($calendarsView) => {
  return transformProviderCalendars($calendarsView.providers);
});

export const emailProvidersStore = derived(calendarsView, ($calendarsView): ProviderInfo[] => {
  if (!constants.GMAIL_SCOPES.length) {
    return [];
  }
  const gmail_scope = constants.GMAIL_SCOPES[0];
  return $calendarsView.providers.filter((provider) =>
    provider.provider.scopes.some((scope: string) => scope.includes(gmail_scope))
  );
});

export const calendarProvidersStore = derived(calendarsView, ($calendarsView): ProviderInfo[] => {
  if (!constants.GOOGLE_CALENDAR_SCOPES.length) {
    return [];
  }
  const calendar_scope = constants.GOOGLE_CALENDAR_SCOPES[0];
  return $calendarsView.providers.filter((provider) =>
    provider.provider.scopes.some((scope: string) => scope.includes(calendar_scope))
  );
});

const startDate = new Date();
const endDate = new Date(startDate);
endDate.setDate(startDate.getDate() + 7);

/* Used by Day/Week/Month viewers to track the selected date range
   Updated immediately when the user changes the date range
*/

export const calendarViewStore = writable<CalendarView>({
  start: startDate.toISOString(),
  end: endDate.toISOString()
});

export const calendarsStatus = derived(
  calendarsViewDataModel,
  ($calendarsViewDataModel) => $calendarsViewDataModel.status
);

// For connecting new providers
export const providerScopeStore = writable<string[] | undefined>([]);

// Export idiomatic methods
export {
  calendarsView,
  calendarsViewDataModel,
  refreshCalendars as loadStoredCalendars,
  refreshCalendars,
  resetCalendarsView,
  setCalendarsView
};
